/**
 * React Query keys for the Events module, and the one place that invalidates them.
 *
 * Module data lives under 'events-hub'. ['event-types'] is shared with the Vendors lookups
 * pages (EventType[] from getAll()), so the Types page invalidates it too.
 */

import type { QueryClient } from '@tanstack/react-query';
import type { EventAdminListQuery } from '@actschurch/ccms-sdk';

export const eventsKeys = {
  root: ['events-hub'] as const,
  lookups: ['events-hub', 'lookups'] as const,
  /** EventType[] (module copy, so a failing Vendors page cannot poison it). */
  types: ['events-hub', 'types'] as const,
  statuses: ['events-hub', 'statuses'] as const,
  adminLists: ['events-hub', 'admin'] as const,
  adminList: (q: EventAdminListQuery) => ['events-hub', 'admin', q] as const,
  detail: (id: number) => ['events-hub', 'detail', id] as const,
  review: ['events-hub', 'review'] as const,
  sources: ['events-hub', 'sources'] as const,
  runs: (connectionId: number) => ['events-hub', 'runs', connectionId] as const,
  mappings: (connectionId: number) => ['events-hub', 'mappings', connectionId] as const,
  suggestions: (connectionId: number) => ['events-hub', 'suggestions', connectionId] as const,
  /** Shared with the Vendors lookups pages. */
  vendorTypes: ['event-types'] as const,
  // Stage 1B
  linkLookups: ['events-hub', 'link-lookups'] as const,
  links: (eventId: number) => ['events-hub', 'links', eventId] as const,
  eventTags: (eventId: number) => ['events-hub', 'event-tags', eventId] as const,
  tags: ['events-hub', 'tags'] as const,
  tagList: (sourceId?: number) => ['events-hub', 'tags', sourceId ?? 'all'] as const,
  rules: ['events-hub', 'rules'] as const,
  rule: (ruleId: number) => ['events-hub', 'rules', ruleId] as const,
  learning: ['events-hub', 'learning'] as const,
  candidates: (statusId?: number) => ['events-hub', 'learning', 'candidates', statusId ?? 'all'] as const,
  learningSettings: ['events-hub', 'learning', 'settings'] as const,
  /** Reverse lookups (department and ministry pages). */
  linkedRoot: ['events-hub', 'linked'] as const,
  linked: (typeId: number, key: string, from?: string, to?: string) => ['events-hub', 'linked', typeId, key, from ?? '', to ?? ''] as const,
  /** Events linked to one record (the "Linked event" chip on task, project and meeting pages). */
  byTarget: (typeId: number, key: string) => ['events-hub', 'linked', 'by-target', typeId, key] as const,
  // Stage 2
  playbooks: ['events-hub', 'playbooks'] as const,
  playbook: (playbookId: number) => ['events-hub', 'playbooks', playbookId] as const,
  playbookSuggestions: (eventId: number) => ['events-hub', 'playbooks', 'suggestions', eventId] as const,
  plans: (eventId: number) => ['events-hub', 'plans', eventId] as const,
  /** Live work item summaries (by-ids) for one link type on one event. */
  workItems: (eventId: number, linkTypeId: number, ids: string) => ['events-hub', 'work-items', eventId, linkTypeId, ids] as const,
  // Stage 3
  /** Playbooks AI suggested for an event (kept client side after "Suggest with AI"; never fetched). */
  aiPlaybookSuggestions: (eventId: number) => ['events-hub', 'ai-playbook-suggestions', eventId] as const,
  /** Forms for the add-link drawer's Form picker. */
  linkForms: ['events-hub', 'pick', 'link-forms'] as const,
  /** Picker lists for the playbook and plan editors (own keys, so other modules' caches stay untouched). */
  pick: (what: string, arg?: string | number | null) => ['events-hub', 'pick', what, arg ?? 'all'] as const,
};

/** After a link, coordinator or review write: the event's links, the lists (suggestion counts) and reverse lookups. */
export function invalidateLinkData(qc: QueryClient, eventId: number) {
  qc.invalidateQueries({ queryKey: eventsKeys.links(eventId) });
  qc.invalidateQueries({ queryKey: eventsKeys.detail(eventId) });
  qc.invalidateQueries({ queryKey: eventsKeys.adminLists });
  qc.invalidateQueries({ queryKey: eventsKeys.linkedRoot });
}

/** After a tag write (the catalogue or an event's tags). */
export function invalidateTagData(qc: QueryClient, eventId?: number) {
  qc.invalidateQueries({ queryKey: eventsKeys.tags });
  if (eventId != null) qc.invalidateQueries({ queryKey: eventsKeys.eventTags(eventId) });
}

/** After a rule write or a learned proposal turned into a rule. */
export function invalidateRuleData(qc: QueryClient) {
  qc.invalidateQueries({ queryKey: eventsKeys.rules });
  qc.invalidateQueries({ queryKey: eventsKeys.learning });
}

/** After an event write: lists, the detail, the review queue and the other modules' event caches. */
export function invalidateEventData(qc: QueryClient, eventId?: number) {
  qc.invalidateQueries({ queryKey: eventsKeys.adminLists });
  qc.invalidateQueries({ queryKey: eventsKeys.review });
  if (eventId != null) qc.invalidateQueries({ queryKey: eventsKeys.detail(eventId) });
  // Vendors and Carpool read events through their own keys.
  qc.invalidateQueries({ queryKey: ['events'] });
  qc.invalidateQueries({ queryKey: ['rides', 'events'] });
}

/** After a mapping write: mappings, suggestions, the review queue and the lists (held events re-resolve). */
export function invalidateMappingData(qc: QueryClient, connectionId: number) {
  qc.invalidateQueries({ queryKey: eventsKeys.mappings(connectionId) });
  qc.invalidateQueries({ queryKey: eventsKeys.suggestions(connectionId) });
  qc.invalidateQueries({ queryKey: eventsKeys.review });
  qc.invalidateQueries({ queryKey: eventsKeys.adminLists });
}

/** After an event type write. */
export function invalidateTypeData(qc: QueryClient) {
  qc.invalidateQueries({ queryKey: eventsKeys.types });
  qc.invalidateQueries({ queryKey: eventsKeys.lookups });
  qc.invalidateQueries({ queryKey: eventsKeys.vendorTypes });
}

/** After a plan write: the event's plans, its links (created work), suggestions and the lists. */
export function invalidatePlanData(qc: QueryClient, eventId: number) {
  qc.invalidateQueries({ queryKey: eventsKeys.plans(eventId) });
  qc.invalidateQueries({ queryKey: eventsKeys.playbookSuggestions(eventId) });
  qc.invalidateQueries({ queryKey: eventsKeys.links(eventId) });
  qc.invalidateQueries({ queryKey: ['events-hub', 'work-items', eventId] });
}

/** After a playbook write: the catalogue and every event's suggestions. */
export function invalidatePlaybookData(qc: QueryClient) {
  qc.invalidateQueries({ queryKey: eventsKeys.playbooks });
}
